/**
 * Property-scoped assignee options for "Assign to" dropdowns.
 * Returns: Self-assign + Team (property_access users) + Cleaners (cleaner_assignments).
 * No revoked/pending; no generic "Team member (team)" placeholder.
 */

import { usePropertyAccess } from "@/hooks/usePropertyAccess";
import { usePropertyCleanerAssignments } from "@/hooks/useCleanerAssignments";
import { usePropertyCleaningAssignment, useCompanyCleanersForProperty } from "@/hooks/useCleaningCompanies";
import { formatPersonLabel } from "@/lib/taskAssigneeLabel";

export const USER_PREFIX = "user:";

export interface AssigneeOption {
  value: string;
  label: string;
  kind: "unassigned" | "self" | "team" | "cleaner";
}

export interface PropertyAssigneeOptionsResult {
  options: AssigneeOption[];
  /** For backward compatibility: self + team as { id, name } with id = user:uuid for team */
  teamMembers: Array<{ id: string; name: string }>;
  /** For backward compatibility: cleaners with property access */
  availableCleaners: Array<{ id: string; name: string }>;
  isLoading: boolean;
}

/**
 * Returns assignee options for a property: Unassigned, Self-assign, Team (accepted users with access), Cleaners (accepted cleaners).
 * Use for Create turnover task, Edit task, and any Assign-to dropdown.
 */
export function usePropertyAssigneeOptions(propertyId: string | undefined): PropertyAssigneeOptionsResult {
  const { data: propertyAccess, isLoading: accessLoading } = usePropertyAccess(propertyId);
  const { data: cleanerAssignments, isLoading: assignmentsLoading } = usePropertyCleanerAssignments(propertyId);
  const { data: companyAssignment } = usePropertyCleaningAssignment(propertyId);
  const { data: companyCleaners } = useCompanyCleanersForProperty(companyAssignment?.company_id);

  const teamMembers = (propertyAccess ?? [])
    .filter((pa) => pa.grantee_type === "user" && pa.grantee_user_id)
    .map((pa) => ({
      id: `${USER_PREFIX}${pa.grantee_user_id}`,
      name: pa.display_name?.trim() ? formatPersonLabel(pa.display_name) || pa.display_name : "Team",
    }));

  const assignedCleaners = ((cleanerAssignments?.map((a) => a.cleaner).filter(Boolean) || []) as Array<{ id: string; name: string }>).map(
    (c) => ({ ...c, name: formatPersonLabel(c.name) || c.name }),
  );
  const availableCleaners = (companyCleaners
    ? [...assignedCleaners, ...(companyCleaners.filter((cc) => !assignedCleaners.some((ac) => ac.id === cc.id)) || [])]
    : assignedCleaners
  ).map((c) => ({ ...c, name: formatPersonLabel(c.name) || c.name }));

  const options: AssigneeOption[] = [
    { value: "unassigned", label: "Unassigned", kind: "unassigned" },
    { value: "self", label: "Self-assign (I'll do it)", kind: "self" },
    ...teamMembers.map((m) => ({ value: m.id, label: `${m.name} (Team Member)`, kind: "team" as const })),
    ...availableCleaners.map((c) => ({ value: c.id, label: `${c.name} (Cleaner)`, kind: "cleaner" as const })),
  ];

  return {
    options,
    teamMembers,
    availableCleaners,
    isLoading: accessLoading || assignmentsLoading,
  };
}
